/**
 * model.js — pure store, reducers, and selectors for the System of Work domain.
 *
 * All functions are pure and return new store objects (immutable-style).
 * No imports from React, WidgetBase, or any UI library.
 *
 * Store shape:
 * {
 *   missionsById:      { [id]: missionVertex },
 *   cellsByMission:    { [missionId]: { [cellId]: cellVertex } },
 *   edgesByMission:    { [missionId]: Array<{ from_id, to_id }> },
 *   metaThreadByCell:  { [cellId]: threadItem[] },
 *   templates: {
 *     workflows:   Array<{ name, start, nodes, edges, defaults }>,
 *     workspaces:  Array<{ name, type, repos, packs }>
 *   },
 *   heartbeat: any | null
 * }
 */

import { TERMINAL_STATUSES } from "./schema";

// ---------------------------------------------------------------------------
// Store factory
// ---------------------------------------------------------------------------

/**
 * Create a new empty store.
 * @returns {Object} initial store
 */
export function createStore() {
  return {
    missionsById: {},
    cellsByMission: {},
    edgesByMission: {},
    metaThreadByCell: {},
    templates: {
      workflows: [],
      workspaces: []
    },
    heartbeat: null
  };
}

// ---------------------------------------------------------------------------
// Reducers — each returns a NEW store, never mutates the input
// ---------------------------------------------------------------------------

/**
 * Replace the full missions collection.
 * @param {Object} store
 * @param {Array} missions - array of mission vertex objects, each with an `id` field
 * @returns {Object} new store
 */
export function setMissions(store, missions) {
  const missionsById = {};
  for (const m of (missions || [])) {
    missionsById[m.id] = m;
  }
  return { ...store, missionsById };
}

/**
 * Insert or update a single mission.
 * @param {Object} store
 * @param {Object} mission - mission vertex object with an `id` field
 * @returns {Object} new store
 */
export function upsertMission(store, mission) {
  return {
    ...store,
    missionsById: { ...store.missionsById, [mission.id]: mission }
  };
}

/**
 * Replace all cells for a given mission.
 * @param {Object} store
 * @param {string} missionId
 * @param {Array} cells - array of cell vertex objects, each with an `element_id` field
 * @returns {Object} new store
 */
export function setCells(store, missionId, cells) {
  const cellMap = {};
  for (const c of (cells || [])) {
    cellMap[c.element_id] = c;
  }
  return {
    ...store,
    cellsByMission: { ...store.cellsByMission, [missionId]: cellMap }
  };
}

/**
 * Replace all "depends_on" edges for a given mission.
 * @param {Object} store
 * @param {string} missionId
 * @param {Array<{ from_id: string, to_id: string }>} edges
 * @returns {Object} new store
 */
export function setDependsOn(store, missionId, edges) {
  return {
    ...store,
    edgesByMission: { ...store.edgesByMission, [missionId]: edges || [] }
  };
}

/**
 * Insert or update a single cell within a mission.
 * @param {Object} store
 * @param {string} missionId
 * @param {Object} cell - cell vertex object with an `element_id` field
 * @returns {Object} new store
 */
export function upsertCell(store, missionId, cell) {
  const existing = store.cellsByMission[missionId] || {};
  return {
    ...store,
    cellsByMission: {
      ...store.cellsByMission,
      [missionId]: { ...existing, [cell.element_id]: cell }
    }
  };
}

/**
 * Remove a single cell from a mission, dropping any depends_on edges that
 * reference it (as either endpoint) so the store never keeps dangling edges.
 * @param {Object} store
 * @param {string} missionId
 * @param {string} cellId - the cell's element_id
 * @returns {Object} new store
 */
export function removeCell(store, missionId, cellId) {
  const existingCells = store.cellsByMission[missionId];
  let cellsByMission = store.cellsByMission;
  if (existingCells && Object.prototype.hasOwnProperty.call(existingCells, cellId)) {
    const nextCells = { ...existingCells };
    delete nextCells[cellId];
    cellsByMission = { ...store.cellsByMission, [missionId]: nextCells };
  }

  const existingEdges = store.edgesByMission[missionId] || [];
  const nextEdges = existingEdges.filter(e => e.from_id !== cellId && e.to_id !== cellId);
  const edgesByMission = nextEdges.length === existingEdges.length
    ? store.edgesByMission
    : { ...store.edgesByMission, [missionId]: nextEdges };

  return { ...store, cellsByMission, edgesByMission };
}

/**
 * Append a single depends_on edge to a mission, de-duplicating on the
 * (from_id, to_id) pair so a dependency is never drawn twice.
 * @param {Object} store
 * @param {string} missionId
 * @param {{ from_id: string, to_id: string, element_id?: string }} edge
 * @returns {Object} new store
 */
export function addDependsOnEdge(store, missionId, edge) {
  if (!edge || edge.from_id == null || edge.to_id == null) return store;
  const existing = store.edgesByMission[missionId] || [];
  if (existing.some(e => e.from_id === edge.from_id && e.to_id === edge.to_id)) {
    return store;
  }
  return {
    ...store,
    edgesByMission: { ...store.edgesByMission, [missionId]: [...existing, edge] }
  };
}

/**
 * Remove the depends_on edge matching (fromId, toId) from a mission.
 * @param {Object} store
 * @param {string} missionId
 * @param {string} fromId - the dependent cell's id
 * @param {string} toId - the prerequisite cell's id
 * @returns {Object} new store
 */
export function removeDependsOnEdge(store, missionId, fromId, toId) {
  const existing = store.edgesByMission[missionId];
  if (!existing) return store;
  const next = existing.filter(e => !(e.from_id === fromId && e.to_id === toId));
  if (next.length === existing.length) return store;
  return {
    ...store,
    edgesByMission: { ...store.edgesByMission, [missionId]: next }
  };
}

/**
 * Replace the meta-thread items for a cell.
 * @param {Object} store
 * @param {string} cellId
 * @param {Array} items - thread item array
 * @returns {Object} new store
 */
export function setMetaThread(store, cellId, items) {
  return {
    ...store,
    metaThreadByCell: { ...store.metaThreadByCell, [cellId]: items || [] }
  };
}

/**
 * Replace the template libraries.
 * @param {Object} store
 * @param {{ workflows: Array, workspaces: Array }} templates
 * @returns {Object} new store
 */
export function setTemplates(store, { workflows, workspaces }) {
  return {
    ...store,
    templates: {
      workflows: Array.isArray(workflows) ? workflows : [],
      workspaces: Array.isArray(workspaces) ? workspaces : []
    }
  };
}

/**
 * Update the heartbeat value.
 * @param {Object} store
 * @param {any} hb - heartbeat_state value
 * @returns {Object} new store
 */
export function setHeartbeat(store, hb) {
  return { ...store, heartbeat: hb };
}

// ---------------------------------------------------------------------------
// Selectors — read-only; return derived data from the store
// ---------------------------------------------------------------------------

/**
 * Return all missions as an array.
 * @param {Object} store
 * @returns {Array}
 */
export function listMissions(store) {
  return Object.values(store.missionsById);
}

/**
 * Return a single mission by id, or undefined.
 * @param {Object} store
 * @param {string} id
 * @returns {Object|undefined}
 */
export function getMission(store, id) {
  return store.missionsById[id];
}

/**
 * Return all cells for a mission as an array.
 * @param {Object} store
 * @param {string} missionId
 * @returns {Array}
 */
export function listCells(store, missionId) {
  const cellMap = store.cellsByMission[missionId];
  if (!cellMap) return [];
  return Object.values(cellMap);
}

/**
 * Return cells for a mission whose status is not terminal.
 * @param {Object} store
 * @param {string} missionId
 * @returns {Array}
 */
export function nonTerminalCells(store, missionId) {
  return listCells(store, missionId).filter(
    c => !TERMINAL_STATUSES.includes(c.props && c.props.status)
  );
}

/**
 * Return a single cell by id within a mission, or undefined.
 * @param {Object} store
 * @param {string} missionId
 * @param {string} cellId
 * @returns {Object|undefined}
 */
export function getCell(store, missionId, cellId) {
  const cellMap = store.cellsByMission[missionId];
  if (!cellMap) return undefined;
  return cellMap[cellId];
}

/**
 * Return the depends_on edges for a mission as an array of { from_id, to_id }.
 * @param {Object} store
 * @param {string} missionId
 * @returns {Array<{ from_id: string, to_id: string }>}
 */
export function listDependsOn(store, missionId) {
  return store.edgesByMission[missionId] || [];
}

/**
 * Return all cells across all missions that have status "input_needed",
 * as a flat array of { missionId, cell } objects.
 * @param {Object} store
 * @returns {Array<{ missionId: string, cell: Object }>}
 */
export function inputNeededCells(store) {
  const result = [];
  for (const missionId of Object.keys(store.cellsByMission)) {
    const cellMap = store.cellsByMission[missionId];
    for (const cell of Object.values(cellMap)) {
      if (cell.props && cell.props.status === "input_needed") {
        result.push({ missionId, cell });
      }
    }
  }
  return result;
}

/**
 * Find a workflow template by name from the templates list.
 * @param {Object} store
 * @param {string} name
 * @returns {Object|undefined}
 */
export function getWorkflowTemplate(store, name) {
  return (store.templates.workflows || []).find(t => t.name === name);
}

/**
 * Find a workspace template by name from the templates list.
 * @param {Object} store
 * @param {string} name
 * @returns {Object|undefined}
 */
export function getWorkspaceTemplate(store, name) {
  return (store.templates.workspaces || []).find(t => t.name === name);
}
